/** /tag-schemas — reusable tag-category JSON schema catalog (grid). */
import AddIcon from '@mui/icons-material/Add';
import { Box, Button } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as tagSchemasApi from '@/api/tagSchemas';
import type { TagSchemaListItem } from '@/api/tagSchemas';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import { orderColumns } from '@/components/DataGrid/columnOrder';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { PageHeader } from '@/components/PageHeader';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { useGridPresets } from '@/hooks/useGridPresets';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { TagSchemaCreateDialog } from './TagSchemaCreateDialog';

const DEFAULT_ORDER = ['id', 'codename', 'name', 'description', 'category_count', 'updated_ts'];

const fmtDate = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString() : '—');

export function TagSchemasListPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_tag_schemas_manage');
  const [createOpen, setCreateOpen] = useState(false);
  useCreateIntent(() => setCreateOpen(true));

  const presets = useGridPresets({
    grid: 'tag_schemas',
    defaultOrder: DEFAULT_ORDER,
    defaultHidden: [],
    defaultOrdering: 'name',
  });

  const columns = useMemo<GridColDef<TagSchemaListItem>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
      copyableColumn({ field: 'codename', headerName: 'Codename', width: 200 }),
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
        renderCell: (p) => (
          <RouterLink to={`/tag-schemas/${p.row.id}`} style={{ color: '#0a1a3f', fontWeight: 500 }}>
            {p.row.name}
          </RouterLink>
        ),
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1.5,
        minWidth: 200,
        sortable: false,
        valueGetter: (_, row) => row.description || '',
      },
      { field: 'category_count', headerName: 'Categories', width: 120 },
      {
        field: 'updated_ts',
        headerName: 'Updated',
        width: 130,
        valueGetter: (_, row) => row.updated_ts,
        renderCell: (p) => fmtDate(p.row.updated_ts),
      },
    ],
    [],
  );

  const orderingFallback = presets.ordering;
  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      tagSchemasApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? orderingFallback ?? 'name',
        search: params.search,
      }),
    [orderingFallback],
  );

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="tag-schemas"
        title="Tag Schemas"
        subtitle="Reusable JSON schemas that define the shape of a tag category's data."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              New schema
            </Button>
          )
        }
      />

      <TagSchemaCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      <ResourceDataGrid<TagSchemaListItem>
        queryKey={['tag_schemas']}
        queryFn={queryFn}
        columns={orderColumns(columns, presets.orderedFields)}
        defaultSort={{ field: 'name', sort: 'asc' }}
        sortModel={presets.sortModel}
        onSortModelChange={presets.onSortModelChange}
        columnVisibilityModel={presets.columnVisibilityModel}
        onColumnVisibilityModelChange={presets.onColumnVisibilityModelChange}
        onColumnOrderChange={presets.onColumnOrderChange}
        emptyMessage="No tag schemas yet."
        headerToolbar={{
          search: true,
          searchPlaceholder: 'Name, codename, description',
          exportFileName: 'tag-schemas',
          presets: presets.toolbar,
        }}
      />
    </Box>
  );
}
